import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subscription, catchError, fromEvent, of, timer } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { CrmAuthApiService } from './crm-auth-api.service';
import { CrmAuthCookieService } from './crm-auth-cookie.service';
import { AuthService } from '../../../../../../../libs/core';

/**
 * Periodically checks the access-token expiry time.
 * When the token is within 60 seconds of expiring, calls the refresh API
 * and persists the new tokens.
 *
 * Also listens for the Page Visibility API 'visibilitychange' event.
 * When the tab becomes visible after being hidden (user returns to a
 * backgrounded tab), it proactively checks whether the token needs
 * refreshing. This prevents the "tab hangs on loading" issue where
 * browser timer throttling in background tabs prevents the periodic
 * check from running, allowing the token to expire undetected.
 */
@Injectable({ providedIn: 'root' })
export class CrmTokenExpiryService implements OnDestroy {
  private readonly checkIntervalMs = 30_000; // check every 30s
  private readonly expiryBufferMs = 60_000; // refresh 60s before actual expiry
  private readonly authService = inject(AuthService);
  private readonly cookieService = inject(CrmAuthCookieService);
  private readonly api = inject(CrmAuthApiService);

  private timerSub: Subscription | null = null;
  private visibilitySub: Subscription | null = null;
  private refreshInFlight = false;

  start(): void {
    if (this.timerSub) return;

    this.timerSub = timer(0, this.checkIntervalMs).subscribe(() =>
      this.checkAndRefresh(),
    );

    // Listen for tab visibility changes.
    // When the user returns to a backgrounded tab, proactively check
    // and refresh the token. This mitigates browser timer throttling
    // in background tabs (Chrome throttles setInterval to ~1/min).
    if (typeof document !== 'undefined') {
      this.visibilitySub = fromEvent(document, 'visibilitychange')
        .pipe(throttleTime(5_000)) // prevent rapid-fire on tab switches
        .subscribe(() => {
          if (document.visibilityState === 'visible') {
            // console.log(
            //   '[TokenExpiry] Tab became visible — checking token freshness.',
            // );
            this.checkAndRefresh();
          }
        });
    }
  }

  stop(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = null;

    this.visibilitySub?.unsubscribe();
    this.visibilitySub = null;
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private checkAndRefresh(): void {
    if (this.refreshInFlight) return;
    if (!this.authService.isAuthenticated()) return;

    const accessExpiresAt = this.cookieService.getAccessTokenExpiresAt();
    const now = Date.now();

    // console.log(
    //   `[TokenExpiry] Current time: ${new Date(now).toISOString()}`,
    // );
    // console.log(
    //   `[TokenExpiry] Access token expires at: ${
    //     accessExpiresAt
    //       ? new Date(accessExpiresAt).toISOString()
    //       : 'unknown'
    //   }`,
    // );

    if (accessExpiresAt === null) {
      // No expiry info — skip
      return;
    }

    const remainingMs = accessExpiresAt - now;
    // console.log(
    //   `[TokenExpiry] Remaining: ${Math.round(remainingMs / 1000)}s`,
    // );

    if (remainingMs > this.expiryBufferMs) {
      // console.log(
      //   `[TokenExpiry] Token still valid — buffer ${this.expiryBufferMs / 1000}s not yet reached.`,
      // );
      return;
    }

    // Expiry within buffer — call refresh
    // console.log('[TokenExpiry] Token expiring soon — calling refresh API...');
    this.doRefresh();
  }

  private doRefresh(): void {
    const refreshToken = this.cookieService.getRefreshToken();
    const refreshTokenExpiresAt = this.cookieService.getRefreshTokenExpiresAt();

    if (!refreshToken) {
      // console.log('[TokenExpiry] No refresh token available — skipping refresh.');
      return;
    }

    // Do not attempt refresh if the refresh token itself is already expired
    if (refreshTokenExpiresAt !== null && refreshTokenExpiresAt <= Date.now()) {
      // console.log('[TokenExpiry] Refresh token has expired — skipping refresh.');
      return;
    }

    this.refreshInFlight = true;

    this.api
      .refreshToken({ refreshToken })
      .pipe(
        catchError((err) => {
          // console.error('[TokenExpiry] Refresh API call failed:', err);
          return of(null);
        }),
      )
      .subscribe((res) => {
        this.refreshInFlight = false;

        if (res?.isSuccess || res?.success) {
          const data = res.data;
          // console.log('[TokenExpiry] Token refreshed successfully.');
          // console.log(
          //   `[TokenExpiry] New access token expires at: ${data.accessTokenExpiresUtc}`,
          // );
          // console.log(
          //   `[TokenExpiry] New refresh token expires at: ${data.refreshTokenExpiresUtc}`,
          // );

          // Persist the new tokens
          this.cookieService.setAuthCookies({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            accessTokenExpiresUtc: data.accessTokenExpiresUtc,
            refreshTokenExpiresUtc: data.refreshTokenExpiresUtc,
          });
        } else {
          // console.log(
          //   '[TokenExpiry] Refresh response was not successful:',
          //   (res as unknown as Record<string, unknown>)?.['message'],
          // );
        }
      });
  }
}